import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import MarkdownLite from "@/lib/MarkdownLite";

/**
 * Loads a policy document by slug from the backend and renders it.
 * Falls back gracefully when offline / not yet seeded.
 */
export default function PolicyPage({ slug, fallbackTitle = "Policy", testId }) {
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/policies/${slug}`)
      .then((res) => {
        if (!cancelled) setPolicy(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const title = policy?.title || fallbackTitle;
  const lastUpdated = policy?.last_updated || "";

  return (
    <div className="bg-[#F7F5F0] min-h-screen" data-testid={testId}>
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Link
          to="/"
          data-testid="policy-back-home"
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] text-[#4A4D4A] hover:text-[#2C392F] mb-10"
        >
          <ArrowLeft size={14} /> Back to ChiMano House
        </Link>

        <div className="text-[11px] uppercase tracking-[0.35em] text-[#985F47] mb-5">ChiMano House</div>
        <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tightest text-[#2C392F] font-light">
          {title}
        </h1>
        {lastUpdated && (
          <p className="mt-4 text-[12px] uppercase tracking-[0.25em] text-[#4A4D4A]">
            Last updated · {lastUpdated}
          </p>
        )}

        <div className="prose prose-stone mt-10 max-w-none policy-content">
          {policy ? (
            <MarkdownLite source={policy.content || ""} />
          ) : error ? (
            <p>This policy is being updated. Please check back shortly.</p>
          ) : (
            <p className="text-[#4A4D4A] italic">Loading…</p>
          )}
        </div>

        <div className="mt-16 border-t border-[#2C392F]/10 pt-8 text-sm text-[#4A4D4A] italic leading-relaxed">
          You can edit this page anytime from <span className="font-medium">Admin → Policies</span>.
        </div>
      </div>

      <style>{`
        .policy-content h2 {
          font-family: 'Cormorant Garamond', serif;
          color: #2C392F;
          font-size: 1.75rem;
          line-height: 1.15;
          margin-top: 2.5rem;
          margin-bottom: 0.9rem;
          font-weight: 500;
        }
        .policy-content p {
          color: #4A4D4A;
          line-height: 1.75;
          margin-bottom: 1.1rem;
          font-size: 16px;
        }
        .policy-content ul {
          list-style: none;
          padding-left: 0;
          margin: 1rem 0 1.4rem;
        }
        .policy-content ul li {
          color: #4A4D4A;
          line-height: 1.7;
          padding-left: 1.4rem;
          position: relative;
          margin-bottom: 0.6rem;
        }
        .policy-content ul li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.7rem;
          width: 0.6rem;
          height: 1px;
          background: #985F47;
        }
        .policy-content strong { color: #2C392F; font-weight: 500; }
      `}</style>
    </div>
  );
}
